import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle, AlertCircle } from "lucide-react";
import { Type, type Props as TypeProps } from "@themes/pollinator/ui/Type";
import { apiRequest, APIError } from "@core/services/api/apiRequest";
import { useCompleteOrgAction, ACTION_TYPE_SLUG } from "@core/features/actions";
import type { InvitationsFormData } from "@core/features/invitation";
import { InvitationForm } from "@platform/features/invitation";

export type Props = {
  organizationId: number;
  /** Invitation ID for sending coworker invitations */
  invitationId: number | null;
  onSuccess?: () => void;
  /** Whether to show the heading (default: true) */
  showHeading?: boolean;
  headingTypeVariant?: TypeProps["variant"];
  descriptionTypeVariant?: TypeProps["variant"];
};

type SendInvitationsPayload = {
  invitation_id: number;
  invitees: Array<{
    email: string;
    first_name: string;
    last_name: string;
  }>;
};

export function CoworkersInvitationFormContainer({
  organizationId,
  invitationId,
  onSuccess,
  showHeading = true,
  headingTypeVariant = "h4",
  descriptionTypeVariant = "body2",
}: Props) {
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { complete: completeSendInvitation } = useCompleteOrgAction({
    actionTypeSlug: ACTION_TYPE_SLUG.SEND_INVITATION,
    organizationId,
  });

  const inviteMutation = useMutation({
    mutationFn: async (data: SendInvitationsPayload) => {
      return await apiRequest("/invitation/send/", "POST", data);
    },
  });

  const handleSubmit = async (data: InvitationsFormData) => {
    setSuccessMessage(null);
    setErrorMessage(null);

    if (!invitationId) {
      setErrorMessage("No invitation configured for this organization.");
      return;
    }

    // Filter out empty items
    const validInvitees = data.items
      .filter((item) => item.email.trim())
      .map((item) => ({
        email: item.email.trim(),
        first_name: item.firstName.trim(),
        last_name: item.lastName.trim(),
      }));

    if (validInvitees.length === 0) {
      setErrorMessage("Please enter at least one email address.");
      return;
    }

    try {
      // Send all invitations in a single request
      await inviteMutation.mutateAsync({
        invitation_id: invitationId,
        invitees: validInvitees,
      });

      setSuccessMessage(
        `Successfully sent ${validInvitees.length} invitation${
          validInvitees.length > 1 ? "s" : ""
        }!`
      );

      // Invalidate queries to refresh the invitation records table
      queryClient.invalidateQueries({
        queryKey: ["organization", organizationId, "coworkers"],
      });
      queryClient.invalidateQueries({
        queryKey: ["invitation-records"],
      });

      // Mark SEND_INVITATION action as completed for this organization
      completeSendInvitation();

      onSuccess?.();
    } catch (error) {
      // The send endpoint returns the specific reason a send was refused
      // (expired, inactive, max uses reached). Showing only a generic failure
      // leaves admins guessing at a cause and chasing the wrong fix.
      const backendMessage =
        error instanceof APIError && error.message !== "Request failed"
          ? error.message
          : null;

      setErrorMessage(
        backendMessage ?? "Failed to send invitations. Please try again."
      );
      console.error("Failed to send coworker invitations:", error);
    }
  };

  const isLoading = inviteMutation.isPending;

  return (
    <div className="flex flex-col gap-6">
      {showHeading && (
        <div className="flex flex-col gap-1">
          <Type variant={headingTypeVariant} as="h3">
            Invite Team Members
          </Type>
          <Type variant={descriptionTypeVariant} className="text-brand-700">
            Add colleagues to help manage your organization. They'll receive an
            email invitation to join.
          </Type>
        </div>
      )}

      {successMessage && (
        <div className="flex items-center gap-2 p-3 rounded-md bg-success-100 text-success-800">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <Type variant="body2">{successMessage}</Type>
        </div>
      )}

      {errorMessage && (
        <div className="flex items-center gap-2 p-3 rounded-md bg-error-100 text-error-800">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <Type variant="body2">{errorMessage}</Type>
        </div>
      )}

      <InvitationForm
        isLoading={isLoading}
        onSubmit={handleSubmit}
        onCancel={() => {
          setSuccessMessage(null);
          setErrorMessage(null);
        }}
        emailPlaceholder="colleague@company.com"
        submitButtonText="Send Invitation"
      />
    </div>
  );
}

export default CoworkersInvitationFormContainer;

